import axios from 'axios';
import { isAfter } from 'date-fns';
import { maintenanceService } from '@/services/axiosConfig';

export const state = {
    drivers: [],
    employees: [],
    cars: [],
    car: {},
    concessions: [],
    modules: [],
    contactPerson: '',
    businessName: '',
    userVerified: true,
    user: {},
    profile: {},
    planType: 'basic',
    planFeatures: {},
    hasMyPos: false,
    maintenanceNotification: null,
    subAccounts: [],
    isSwitchingAccount: false,
    enums: {
        paymentMethods: [],
        dispatchPlatforms: [],
        tripTypes: [],
    },
    enumsLoaded: false,
    enumsLoading: false,
};

export const mutations = {
    setDrivers(state, drivers) {
        state.drivers = drivers;
    },
    setCars(state, cars) {
        state.cars = cars;
    },
    setConcessions(state, concessions) {
        state.concessions = concessions;
    },
    setModules(state, modules) {
        state.modules = modules;
    },
    setContactPerson(state, contactPerson) {
        state.contactPerson = contactPerson;
    },
    setBusinessName(state, businessName) {
        state.businessName = businessName;
    },
    setUserVerified(state, userVerified) {
        state.userVerified = userVerified;
    },
    setUser(state, user) {
        state.user = user;
    },
    setEmployees(state, employees) {
        state.employees = employees;
    },
    setCar(state, car) {
        state.car = car;
    },
    setPlanType(state, planType) {
        state.planType = planType;
    },
    setPlanFeatures(state, planFeatures) {
        state.planFeatures = planFeatures;
    },
    setHasMyPos(state, hasMyPos) {
        state.hasMyPos = hasMyPos;
    },
    setMaintenanceNotification(state, notification) {
        state.maintenanceNotification = notification;
    },
    setEnums(state, enums) {
        state.enums = enums;
        state.enumsLoaded = true;
    },
    setEnumsLoading(state, loading) {
        state.enumsLoading = loading;
    },
    setSubAccounts(state, subAccounts) {
        state.subAccounts = subAccounts || [];
    },
    setIsSwitchingAccount(state, isSwitching) {
        state.isSwitchingAccount = isSwitching;
    },
    setProfile(state, profile) {
        state.profile = profile;
    },
    updateProfile(state, profileData) {
        state.profile = { ...state.profile, ...profileData };
    },
};

export const actions = {
    async getProfile({ commit, state }) {
        try {
            const response = await axios.get(
                `${import.meta.env.VITE_API_BASE_URL}/cpanel/profile/simple`,
                {
                    withCredentials: true,
                },
            );
            localStorage.setItem('authToken', 'token');

            const drivers = response.data.drivers.sort((a, b) => a?.value.localeCompare(b?.value));
            const cars = response.data.licenseNumbers.sort((a, b) =>
                a?.value.localeCompare(b?.value),
            );

            const concessions = response.data.concessions
                ? response.data.concessions.sort((a, b) => a?.value - b?.value)
                : [];
            const dataStoragePlan = response.data.dataStoragePlan;
            const planTypes = ['basic', 'lite', 'premium'];
            const planType = planTypes.find((plan) => dataStoragePlan.includes(plan)) || 'basic';
            const moduleKeys = response.data.modules.map((module) => module.staticId);
            commit('setDrivers', drivers);
            if (state.cars.length === 0) {
                commit('setCars', cars);
            }
            commit('setConcessions', concessions);
            commit('setModules', moduleKeys);
            commit('setContactPerson', response.data.contactPerson);
            commit('setBusinessName', response.data.businessName);
            commit('setUserVerified', response.data.isVerified);
            commit('setUser', response.data);
            commit('setPlanType', planType);
            commit('setHasMyPos', response.data.hasMyPos || false);

            // Set available features based on plan type
            const planFeatures = determinePlanFeatures(planType);
            commit('setPlanFeatures', planFeatures);
            commit('setSubAccounts', response.data.subAccounts || []);

            localStorage.setItem('drivers', JSON.stringify(drivers));
            localStorage.setItem('cars', JSON.stringify(cars));
            localStorage.setItem('concessions', JSON.stringify(concessions));

            return response.data;
        } catch (error) {
            if (error.isMaintenance) {
                return;
            }
            this.$toasted?.show('Ein Fehler ist aufgetreten.', { type: 'error' });
        }
    },
    async getDrivers({ commit }) {
        try {
            const response = await axios.get(
                `${import.meta.env.VITE_API_BASE_URL}/cpanel/profile/simple`,
                {
                    withCredentials: true,
                },
            );
            commit('setDrivers', response.data.drivers);
        } catch (error) {
            if (error.isMaintenance) {
                return;
            }
            this.$toasted?.show('Ein Fehler ist aufgetreten.', { type: 'error' });
        }
    },
    async getEmployees({ commit }) {
        try {
            const url = `${import.meta.env.VITE_API_BASE_URL}/cpanel/drivers`;
            const result = await axios.get(url, {
                withCredentials: true,
            });

            commit(
                'setEmployees',
                result.data.filter((d) => d?.isVisible),
            );
        } catch (error) {
            if (error.isMaintenance) {
                return;
            }
            this.$toasted?.show('Ein Fehler ist aufgetreten.', { type: 'error' });
        }
    },
    async getCars({ commit }) {
        try {
            const url = `${import.meta.env.VITE_API_BASE_URL}/cpanel/cars`;
            const result = await axios.get(url, {
                withCredentials: true,
            });

            const response = result.data;
            const cars = response.map((car) => ({
                ...car,
                id: car.currentInstallation?.licenseNumber || car.installations?.[0]?.licenseNumber,
                value:
                    car.currentInstallation?.licenseNumber || car.installations?.[0]?.licenseNumber,
                uuid: car.uuid,
            }));
            commit('setCars', cars);
        } catch (error) {
            // Check if it's a maintenance error
            if (error.isMaintenance) {
                // Don't show error message - let global maintenance handler work
                return;
            }
            // Only show error for non-maintenance issues
            this.$toasted?.show('Ein Fehler ist aufgetreten.', { type: 'error' });
        }
    },
    async getCar({ commit }, uuid) {
        try {
            const url = `${import.meta.env.VITE_API_BASE_URL}/cpanel/cars/${uuid}`;
            const result = await axios.get(url, {
                withCredentials: true,
            });

            const response = result.data;
            commit('setCar', response);
        } catch (error) {
            if (error.isMaintenance) {
                return;
            }
            this.$toasted?.show('Ein Fehler ist aufgetreten.', { type: 'error' });
        }
    },
    async getModules({ commit }) {
        try {
            const response = await axios.get(
                `${import.meta.env.VITE_API_BASE_URL}/cpanel/profile/simple`,
                {
                    withCredentials: true,
                },
            );
            commit('setModules', response.data.modules);
        } catch (error) {
            if (error.isMaintenance) {
                return;
            }
            this.$toasted?.show('Ein Fehler ist aufgetreten.', { type: 'error' });
        }
    },
    async getContactPerson({ commit }) {
        try {
            const response = await axios.get(
                `${import.meta.env.VITE_API_BASE_URL}/cpanel/profile/simple`,
                {
                    withCredentials: true,
                },
            );
            commit('setContactPerson', response.data.contactPerson);
        } catch (error) {
            if (error.isMaintenance) {
                return;
            }
            this.$toasted?.show('Ein Fehler ist aufgetreten.', { type: 'error' });
        }
    },
    async getBusinessName({ commit }) {
        try {
            const response = await axios.get(
                `${import.meta.env.VITE_API_BASE_URL}/cpanel/profile/simple`,
                {
                    withCredentials: true,
                },
            );
            commit('setBusinessName', response.data.businessName);
        } catch (error) {
            if (error.isMaintenance) {
                return;
            }
            this.$toasted?.show('Ein Fehler ist aufgetreten.', { type: 'error' });
        }
    },

    setDrivers({ commit }, drivers) {
        commit('setDrivers', drivers);
    },
    setEmployees({ commit }, employees) {
        commit('setEmployees', employees);
    },
    setCars({ commit }, cars) {
        commit('setCars', cars);
    },
    setConcessions({ commit }, concessions) {
        commit('setConcessions', concessions);
    },
    setPlanType({ commit }, planType) {
        commit('setPlanType', planType);
        const planFeatures = determinePlanFeatures(planType);
        commit('setPlanFeatures', planFeatures);
    },
    checkMaintenanceNotification({ commit }) {
        const status = maintenanceService.getMaintenanceStatus();

        if (status.maintenanceWindow && status.maintenanceWindow.from) {
            const now = new Date();
            const maintenanceDate = status.maintenanceWindow.from;

            // Check if maintenance is scheduled for the future
            if (isAfter(maintenanceDate, now)) {
                commit('setMaintenanceNotification', {
                    message: 'Wartung geplant',
                    scheduledDate: {
                        startDate: status.maintenanceWindow.from,
                        endDate: status.maintenanceWindow.to,
                    },
                    isFuture: true,
                });
            } else {
                commit('setMaintenanceNotification', null);
            }
        } else {
            commit('setMaintenanceNotification', null);
        }
    },
    async fetchEnums({ commit, state }) {
        // If already loaded or currently loading, don't fetch again
        if (state.enumsLoaded || state.enumsLoading) {
            return state.enums;
        }

        try {
            commit('setEnumsLoading', true);
            const url = `${import.meta.env.VITE_API_BASE_URL}/enums`;
            const result = await axios.get(url, {
                withCredentials: true,
            });

            if (result.data) {
                const enums = {
                    paymentMethods: result.data.paymentMethods || [],
                    dispatchPlatforms: result.data.dispatchPlatforms || [],
                    tripTypes: result.data.tripTypes || [],
                };
                commit('setEnums', enums);
                return enums;
            }
        } catch (error) {
            console.warn('Failed to load enums from API, using fallbacks:', error);
            // Return fallback enums
            const fallbackEnums = {
                paymentMethods: [
                    { name: 'Barzahlung', value: 'cash' },
                    { name: 'Uber', value: 'uber' },
                    { name: 'Bolt', value: 'bolt' },
                    { name: 'Bliq', value: 'bliq' },
                    { name: 'FreeNow', value: 'freenow' },
                    { name: 'Coupon', value: 'coupon' },
                    { name: 'taxi.eu Payment', value: 'taxieupayment' },
                    { name: 'Taxi Deutschland', value: 'taxideutschland' },
                    { name: 'Kartenzahlung', value: 'card' },
                    { name: 'Rechnung', value: 'invoice' },
                ],
                dispatchPlatforms: [
                    { name: 'Keine', value: 'none' },
                    { name: 'Funk', value: 'funk' },
                    { name: 'Uber', value: 'uber' },
                    { name: 'FreeNow', value: 'freenow' },
                    { name: 'Bolt', value: 'bolt' },
                    { name: 'Bliq', value: 'bliq' },
                ],
                tripTypes: [
                    { name: 'Normal', value: 'normal', data: { vatPercent: 7 } },
                    { name: 'Leerfahrt', value: 'empty', data: { vatPercent: 0 } },
                    { name: 'Boten-/Kurierfahrt', value: 'courier', data: { vatPercent: 19 } },
                    { name: 'Materialfahrt', value: 'material', data: { vatPercent: 19 } },
                    { name: 'Krankenfahrt', value: 'patient', data: { vatPercent: 7 } },
                    {
                        name: 'Langstrecke (50km+)',
                        value: 'long_distance',
                        data: { vatPercent: 19 },
                    },
                ],
            };
            commit('setEnums', fallbackEnums);
            return fallbackEnums;
        } finally {
            commit('setEnumsLoading', false);
        }
    },
    async switchAccount({ commit, dispatch, state }, customerNumber) {
        commit('setIsSwitchingAccount', true);
        try {
            const response = await axios.get(
                `${import.meta.env.VITE_API_BASE_URL}/cpanel/switch-account/${customerNumber}`,
                {
                    withCredentials: true,
                },
            );

            if (response.status === 204 || response.status === 200) {
                dispatch('apiCache/clearAllCache', null, { root: true });
                await dispatch('getProfile');

                commit('setDrivers', []);
                commit('setCars', []);
                commit('setEmployees', []);

                return true;
            }

            return false;
        } catch (error) {
            if (error.isMaintenance) {
                commit('setIsSwitchingAccount', false);
                return false;
            }

            commit('setIsSwitchingAccount', false);
            throw error;
        } finally {
            commit('setIsSwitchingAccount', false);
        }
    },
    async getFullProfile({ commit }) {
        try {
            const response = await axios.get(
                `${import.meta.env.VITE_API_BASE_URL}/cpanel/profile`,
                {
                    withCredentials: true,
                },
            );
            commit('setProfile', response.data);
            return response.data;
        } catch (error) {
            console.error('Error fetching full profile:', error);
            throw error;
        }
    },
    async updateCompanyData({ commit }, payload) {
        try {
            const response = await axios.patch(
                `${import.meta.env.VITE_API_BASE_URL}/cpanel/profile/company_data`,
                payload,
                {
                    withCredentials: true,
                },
            );

            commit('updateProfile', payload);
            return response.data;
        } catch (error) {
            console.error('Error updating company data:', error);
            throw error;
        }
    },
};

export const getters = {
    employees: (state) => state.employees,
    drivers: (state) => state.drivers,
    availableDrivers: (state) => {
        return state.drivers.filter((driver) => {
            const isBetrieb = driver.driverNumber === 8888 || driver.id === 8888;
            const isWerkstatt = driver.driverNumber === 9999 || driver.id === 9999;
            return !isBetrieb && !isWerkstatt;
        });
    },
    cars: (state) => state.cars,
    car: (state) => state.car,
    concessions: (state) => state.concessions,
    modules: (state) => state.modules,
    contactPerson: (state) => state.contactPerson,
    businessName: (state) => state.businessName,
    userVerified: (state) => state.userVerified,
    user: (state) => state.user,
    profile: (state) => state.profile,
    planType: (state) => state.planType,
    planFeatures: (state) => state.planFeatures,
    dataStoragePlanLabel: (state) => state.user?.dataStoragePlanLabel || null,
    hasMyPos: (state) => state.hasMyPos,
    maintenanceNotification: (state) => state.maintenanceNotification,
    // Plan type getters
    isPlanLite: (state) => state.planType === 'lite',
    isPlanBasic: (state) => state.planType === 'basic',
    isPlanPro: (state) => state.planType === 'pro',
    isPlanUltimate: (state) => state.planType === 'ultimate',
    // Feature access getters
    canAddDriver: (state) => state.employees.length < state.planFeatures.maxDrivers,
    canAddVehicle: (state) => state.cars.length < state.planFeatures.maxVehicles,
    hasFeature: (state) => (featureName) => state.planFeatures[featureName] === true,
    // Enum getters
    enums: (state) => state.enums,
    enumsLoaded: (state) => state.enumsLoaded,
    enumsLoading: (state) => state.enumsLoading,
    paymentMethods: (state) => state.enums.paymentMethods,
    dispatchPlatforms: (state) => state.enums.dispatchPlatforms,
    tripTypes: (state) => state.enums.tripTypes,
    isAlleinfahrenderEU: (state) => {
        const isEinzelunternehmen = state.profile?.legalForm === 'Einzelunternehmen';
        const nonSystemDrivers = state.employees?.filter((d) => !d.isSystemDriver) || [];
        const isSingleDriver = nonSystemDrivers.length === 1 || nonSystemDrivers.length === 0;
        return isEinzelunternehmen && isSingleDriver;
    },
    // Car status getters
    activeCars: (state) => {
        return state.cars
            .reduce((result, car) => {
                if (car.currentInstallation && car.currentInstallation.licenseNumber) {
                    result.push(car);
                }
                return result;
            }, [])
            .sort((a, b) =>
                a.currentInstallation.licenseNumber.localeCompare(
                    b.currentInstallation.licenseNumber,
                ),
            );
    },
    inactiveCars: (state) => {
        return state.cars
            .reduce((result, car) => {
                if (
                    car.currentInstallation === null &&
                    car.installations &&
                    Array.isArray(car.installations) &&
                    car.installations.length > 0
                ) {
                    const firstInstallation = car.installations[0];
                    const transformedCar = {
                        ...car,
                        id: firstInstallation.licenseNumber,
                        value: firstInstallation.licenseNumber,
                    };

                    if (transformedCar.value) {
                        result.push(transformedCar);
                    }
                }

                return result;
            }, [])
            .sort((a, b) => a.value.localeCompare(b.value));
    },
    subAccounts: (state) => state.subAccounts,
    isSwitchingAccount: (state) => state.isSwitchingAccount,
    availableAccounts: (state) => {
        const currentCustomerNumber = state.user?.customerNumber;
        const subAccounts = (state.subAccounts || []).map((account) => ({
            ...account,
            isCurrent: account.customerNumber === currentCustomerNumber,
        }));

        const availableSubAccounts = subAccounts.filter(
            (account) => account.customerNumber !== currentCustomerNumber,
        );

        if (availableSubAccounts.length === 0) {
            return [];
        }

        return availableSubAccounts;
    },
};

function determinePlanFeatures(planType) {
    const features = {
        lite: {
            maxDrivers: 3,
            maxVehicles: 5,
            advancedReporting: false,
            liveTracking: false,
            geoFencing: false,
            apiAccess: false,
        },
        basic: {
            maxDrivers: 10,
            maxVehicles: 20,
            advancedReporting: true,
            liveTracking: true,
            geoFencing: false,
            apiAccess: false,
        },
        pro: {
            maxDrivers: 25,
            maxVehicles: 50,
            advancedReporting: true,
            liveTracking: true,
            geoFencing: true,
            apiAccess: false,
        },
        ultimate: {
            maxDrivers: 50,
            maxVehicles: 100,
            advancedReporting: true,
            liveTracking: true,
            geoFencing: true,
            apiAccess: true,
        },
    };

    return features[planType] || features.lite;
}
